import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react';

import type { VehicleData } from 'Pages/vehicles/helpers/vehicle.schema';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import { useRealtimeBiddingData } from './useRealtimeBiddingData';

type VehicleDataContextType = {
	vehicle: VehicleData;
};

const VehicleDataContext = createContext<Nullable<VehicleDataContextType>>(null);

type VehicleDataProviderProps = {
	children: ReactNode;
	vehicle: VehicleData;
};

export const VehicleDataProvider: React.FC<VehicleDataProviderProps> = ({ children, vehicle }) => {
	const isServerScenarioEnabled = useFeatureToggle(FEATURES.serverScenarioSelector);
	const vehicleWithRealtimeBiddingUpdates = useRealtimeBiddingData(vehicle);

	// Disable socket updates when running mocked scenarios
	const vehicleData = isServerScenarioEnabled ? vehicle : vehicleWithRealtimeBiddingUpdates;

	return <VehicleDataContext.Provider value={{ vehicle: vehicleData }}>{children}</VehicleDataContext.Provider>;
};

export const useVehicleData = (): VehicleDataContextType['vehicle'] => {
	const context = useContext(VehicleDataContext);

	if (!context) {
		throw new Error('useVehicleData must be used inside VehicleProvider');
	}

	return context.vehicle;
};
